import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

import { G6_MATERIALS_CRUD, DEBTOR_REMINDERS } from 'core/featureFlags';

/**
 * Capability booleans for the Group Dashboard, derived from the current user's
 * roles. Hiding a control is a UX affordance only — the backend remains the
 * authority on each mutation/export.
 */
export interface IGroupPermissions {
  /** Admin, FinanceManager, AccountManager (SalesManager: read). */
  canViewFinance: boolean;
  /** Same set as `canViewFinance`. */
  canExportFinance: boolean;
  /** Admin, AccountManager, SalesManager, FinanceManager. */
  canTerminate: boolean;
  /** (Admin, FinanceManager, AccountManager) AND the `DEBTOR_REMINDERS` flag. */
  canRemindDebtors: boolean;
  /** (Admin, Methodist, Teacher) AND the `G6_MATERIALS_CRUD` flag. */
  canManageMaterials: boolean;
}

const hasAnyRole = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/**
 * Reads `userStateVar().me.roles` and maps `UserRole` to capability booleans,
 * mirroring the backend TZ permission notes.
 */
export const useGroupPermissions = (): IGroupPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];

  const canViewFinance = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.FINANCIER,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
  ]);

  const canTerminate = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
    UserRole.FINANCIER,
  ]);

  const canRemindDebtors =
    DEBTOR_REMINDERS &&
    hasAnyRole(roles, [
      UserRole.ADMIN,
      UserRole.FINANCIER,
      UserRole.ACCOUNTANT,
    ]);

  const canManageMaterials =
    G6_MATERIALS_CRUD &&
    hasAnyRole(roles, [UserRole.ADMIN, UserRole.METHODIST, UserRole.TEACHER]);

  return {
    canViewFinance,
    canExportFinance: canViewFinance,
    canTerminate,
    canRemindDebtors,
    canManageMaterials,
  };
};
